import { ReactNode, SyntheticEvent, useState } from 'react'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { StorePart } from '../../../../types/storePart'
import {
	AllocatedTable,
	OrderedTable,
	RentalsTable,
	OutgoingTable,
	matchReservation,
	matchOrder,
	matchRental,
	matchOutgoing,
} from './CommitmentTables'

export type CommitmentTab = 'allocated' | 'ordered' | 'onRent' | 'outgoing'

// Order mirrors the State panel: Committed (allocated, outgoing, on rent) then Incoming (ordered).
export const COMMITMENT_TABS: { key: CommitmentTab, label: string }[] = [
	{ key: 'allocated', label: 'Allocated' },
	{ key: 'outgoing', label: 'Outgoing' },
	{ key: 'onRent', label: 'On rent' },
	{ key: 'ordered', label: 'Ordered' },
]

/** Ordered (open POs) is incoming, not an outbound commitment — set it off in the tab bar like the panel does. */
const INCOMING_TAB: CommitmentTab = 'ordered'

/** Unit totals per commitment category — used for the stat tiles and the resting tab labels. */
export function getCommitmentCounts( item: StorePart ): Record<CommitmentTab, number> {
	return {
		allocated: ( item.openReservations ?? [] ).reduce( ( sum, r ) => sum + r.quantity, 0 ),
		ordered: ( item.partsOrderedDetails ?? [] ).reduce( ( sum, o ) => sum + o.quantityOrdered, 0 ),
		onRent: ( item.rentalItems ?? [] ).reduce( ( sum, r ) => sum + r.quantityOnRent, 0 ),
		outgoing: ( item.outgoingTransferDetails ?? [] ).reduce( ( sum, t ) => sum + t.quantity, 0 ),
	}
}

/** Matching-row counts per category for a (trimmed, lower-cased) query — drives the holistic search. */
function getMatchCounts( item: StorePart, query: string ): Record<CommitmentTab, number> {
	return {
		allocated: ( item.openReservations ?? [] ).filter( ( r ) => matchReservation( r, query ) ).length,
		ordered: ( item.partsOrderedDetails ?? [] ).filter( ( o ) => matchOrder( o, query ) ).length,
		onRent: ( item.rentalItems ?? [] ).filter( ( r ) => matchRental( r, query ) ).length,
		outgoing: ( item.outgoingTransferDetails ?? [] ).filter( ( t ) => matchOutgoing( t, query ) ).length,
	}
}

const EMPTY_MESSAGE: Record<CommitmentTab, string> = {
	allocated: 'Nothing allocated right now.',
	ordered: 'No open purchase orders.',
	onRent: 'Nothing out on rent.',
	outgoing: 'No open outgoing transfers.',
}

function EmptyState( { message }: { message: string } ) {
	return (
		<Box sx={{ p: 4, textAlign: 'center' }}>
			<Typography variant="body2" color="text.secondary">{message}</Typography>
		</Box>
	)
}

function TabContent( { tab, item, search }: { tab: CommitmentTab, item: StorePart, search: string } ) {
	const query = search.trim().toLowerCase()
	const reservations = item.openReservations ?? []
	const orders = item.partsOrderedDetails ?? []
	const rentals = item.rentalItems ?? []
	const transfers = item.outgoingTransferDetails ?? []

	const rawCount = {
		allocated: reservations.length,
		ordered: orders.length,
		onRent: rentals.length,
		outgoing: transfers.length,
	}[ tab ]

	if ( rawCount === 0 ) {
		return <EmptyState message={EMPTY_MESSAGE[ tab ]} />
	}

	let node: ReactNode = null
	let matchCount = 0
	switch ( tab ) {
		case 'allocated': {
			const rows = query ? reservations.filter( ( r ) => matchReservation( r, query ) ) : reservations
			matchCount = rows.length
			node = <AllocatedTable reservations={rows} />
			break
		}
		case 'ordered': {
			const rows = query ? orders.filter( ( o ) => matchOrder( o, query ) ) : orders
			matchCount = rows.length
			node = <OrderedTable orders={rows} />
			break
		}
		case 'onRent': {
			const rows = query ? rentals.filter( ( r ) => matchRental( r, query ) ) : rentals
			matchCount = rows.length
			node = <RentalsTable rentals={rows} />
			break
		}
		case 'outgoing': {
			const rows = query ? transfers.filter( ( t ) => matchOutgoing( t, query ) ) : transfers
			matchCount = rows.length
			node = <OutgoingTable transfers={rows} />
			break
		}
		default:
			break
	}

	if ( matchCount === 0 ) {
		return <EmptyState message={`No matches for “${search.trim()}” in this list.`} />
	}

	return <Box sx={{ p: 2, overflowX: 'auto' }}>{node}</Box>
}

export type CommitmentsDrawerProps = {
	open: boolean,
	activeTab: CommitmentTab,
	onTabChange: ( tab: CommitmentTab ) => void,
	onClose: () => void,
	item: StorePart,
}

/**
 * Roomy replacement for the four cramped commitment popovers. Consolidates Allocated /
 * Ordered / On rent / Outgoing into one full-height right-side drawer with tabs — the
 * in-flight "what's coming and going" counterpart to the settled Movement History.
 *
 * Holistic client-side search: one query filters across all four categories at once. Tab
 * labels show per-tab match counts (tabs with none are dimmed), and typing jumps to the
 * first tab with a hit when the current tab has none. Built for DC-scale lists; the
 * reservation "Move to stock" action gets real space. All MUI — no EUI.
 */
export default function CommitmentsDrawer( { open, activeTab, onTabChange, onClose, item }: CommitmentsDrawerProps ) {
	const [ search, setSearch ] = useState( '' )

	const query = search.trim().toLowerCase()
	const counts = getCommitmentCounts( item )
	const matchCounts = query ? getMatchCounts( item, query ) : null
	const totalMatches = matchCounts ? COMMITMENT_TABS.reduce( ( sum, { key } ) => sum + matchCounts[ key ], 0 ) : 0

	const handleSearchChange = ( value: string ) => {
		setSearch( value )
		const nextQuery = value.trim().toLowerCase()
		if ( !nextQuery ) {
			return
		}
		// Holistic: if the current tab has no hits but another does, surface the results.
		const nextCounts = getMatchCounts( item, nextQuery )
		if ( nextCounts[ activeTab ] === 0 ) {
			const firstHit = COMMITMENT_TABS.find( ( { key } ) => nextCounts[ key ] > 0 )
			if ( firstHit ) {
				onTabChange( firstHit.key )
			}
		}
	}

	const handleTabChange = ( _event: SyntheticEvent, value: CommitmentTab ) => onTabChange( value )

	return (
		<Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
				<Box>
					<Typography variant="h6">In-flight</Typography>
					<Typography variant="body2" color="text.secondary">
						Committed and incoming units at <Box component="strong" fontWeight={700}>{item.inventoryLocation.name}</Box> — not yet settled.
					</Typography>
				</Box>
				<IconButton onClick={onClose} aria-label="Close in-flight panel">
					<CloseIcon />
				</IconButton>
			</Stack>
			<Divider />
			<Box sx={{ px: 2, py: 1.5 }}>
				<TextField
					value={search}
					onChange={( event ) => handleSearchChange( event.target.value )}
					placeholder="Search PO, TO, WO, order…"
					size="small"
					fullWidth
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						),
						endAdornment: search ? (
							<InputAdornment position="end">
								<IconButton size="small" onClick={() => setSearch( '' )} aria-label="Clear search">
									<CloseIcon fontSize="small" />
								</IconButton>
							</InputAdornment>
						) : undefined,
					}}
				/>
				{query && (
					<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
						{totalMatches} {totalMatches === 1 ? 'match' : 'matches'} across all lists
					</Typography>
				)}
			</Box>
			<Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
				{COMMITMENT_TABS.map( ( { key, label } ) => {
					// While searching show match counts; at rest show unit totals. Dim when it's zero.
					const count = matchCounts ? matchCounts[ key ] : counts[ key ]
					return (
						<Tab
							key={key}
							value={key}
							label={
								<Box sx={{ lineHeight: 1.3 }}>
									<Box component="span" sx={{ display: 'block' }}>{label}</Box>
									{/* Lightweight but scannable: light weight + muted, tabular figures so counts
									    (designed for up to 4 digits) line up tab-to-tab. */}
									<Box
										component="span"
										sx={{ display: 'block', mt: 0.25, fontSize: 13, fontWeight: 300, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
									>
										{count.toLocaleString( 'en-US' )}
									</Box>
								</Box>
							}
							sx={{
								opacity: count === 0 ? 0.5 : 1,
								// Set the incoming tab off from the outbound commitments, mirroring the panel divider.
								// The tabs' own padding supplies the gap, so only the 1px rule is added (keeps all four visible).
								...( key === INCOMING_TAB && {
									borderLeftWidth: '1px',
									borderLeftStyle: 'solid',
									borderColor: 'divider',
								} ),
							}}
						/>
					)
				} )}
			</Tabs>
			<Divider />
			{/* flex:1 + minHeight:0 so ONLY this region scrolls. Without it the drawer Paper (a flex
			    column) shrinks the header/search/tabs when a list overflows — which changed the tab-bar
			    height when switching between a long list and an empty tab. */}
			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
				<TabContent tab={activeTab} item={item} search={search} />
			</Box>
		</Drawer>
	)
}
